import {
  DeesElement,
  customElement,
  property,
  state,
  html,
  css,
  directives,
} from '@design.estate/dees-element';
import type { IKanbanColumn, IKanbanTicket } from '../interfaces.js';
import type { IKanbanMotion } from '../kanban.dragcontroller.js';
import { kanbanStyles } from '../kanban.styles.js';
import { kanbanEvent, sortKanbanTickets } from '../kanban.helpers.js';
import '../dees-kanban-card/dees-kanban-card.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-kanban-column': DeesKanbanColumn;
  }
}

@customElement('dees-kanban-column')
export class DeesKanbanColumn extends DeesElement {
  @property({ attribute: false })
  accessor column: IKanbanColumn | undefined;

  @property({ attribute: false })
  accessor tickets: IKanbanTicket[] = [];

  @property({ attribute: false })
  accessor allTickets: IKanbanTicket[] = [];

  @property({ attribute: false })
  accessor columns: IKanbanColumn[] = [];

  @property({ type: Boolean })
  accessor movable = false;

  @property({ type: Boolean })
  accessor movesDisabled = false;

  @property({ type: Boolean })
  accessor canCreate = false;

  @property({ type: String })
  accessor pendingTicketId = '';

  @property({ type: String })
  accessor draggingTicketId = '';

  @property({ attribute: false })
  accessor pointerManaged = false;

  @state()
  private accessor nativeDropBefore: string | undefined;

  @state()
  private accessor nativeOver = false;

  /** Internal render projection; provider-owned ticket data remains unchanged until saved. */
  @property({ attribute: false })
  accessor motion: IKanbanMotion | undefined;

  public static styles = [
    ...kanbanStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        min-width: 272px;
        max-width: 360px;
        flex: 1 0 272px;
      }

      section {
        min-height: 220px;
        height: 100%;
        border-radius: var(--dees-radius-xl);
        transition: background var(--dees-transition-default) var(--dees-ease-out);
      }

      header {
        display: flex;
        align-items: center;
        gap: 8px;
        min-height: 40px;
        margin-bottom: 10px;
        padding: 0 2px 8px;
        border-bottom: 1px solid var(--dees-color-border-subtle);
      }

      h3 {
        font-size: var(--dees-font-footnote-size);
        font-weight: 600;
      }

      header dees-button::part(button) {
        width: 24px;
        height: 24px;
        min-height: 24px;
      }

      .status {
        width: 8px;
        height: 8px;
        border: 1.5px solid var(--dees-color-text-secondary);
        border-radius: 50%;
        flex-shrink: 0;
      }

      .status.done {
        background: var(--dees-color-accent-success);
        border-color: var(--dees-color-accent-success);
      }

      .stack {
        display: grid;
        gap: 8px;
        min-height: 120px;
        align-content: start;
      }

      .target {
        position: relative;
      }

      .native-before::before {
        content: '';
        position: absolute;
        top: -5px;
        left: 0;
        right: 0;
        height: 2px;
        background: var(--dees-color-accent-primary);
        border-radius: 2px;
      }

      .placeholder {
        border-radius: var(--dees-radius-xl);
        background: var(--dees-color-fill-tertiary);
        box-shadow: inset 0 0 0 1px var(--dees-color-border-default);
      }

      .end-target {
        position: relative;
        min-height: 32px;
        margin-top: 8px;
      }

      section.over {
        background: var(--dees-color-row-hover);
      }

      @media (prefers-reduced-motion: reduce) {
        section {
          transition: none;
        }
      }

      @media (pointer: coarse) {
        header dees-button::part(button) {
          width: 32px;
          height: 32px;
          min-height: 32px;
        }
      }
    `,
  ];

  public updated(): void {
    if (!this.draggingTicketId) this.nativeOver = false;
  }

  public render() {
    if (!this.column) return html``;
    const motion = this.motion;
    const tickets = sortKanbanTickets(this.tickets).filter(
      ticket => ticket.id !== motion?.move.ticketId,
    );
    const active = motion?.move.columnId === this.column.id;
    if (motion && active) {
      const index = tickets.findIndex(ticket => ticket.id === motion.move.beforeTicketId);
      tickets.splice(index < 0 ? tickets.length : index, 0, motion.ticket);
    }
    return html`<section
      class=${active && motion?.placeholder || this.nativeOver ? 'over' : ''}
      aria-label=${this.column.title}
      @dragover=${(event: DragEvent) => {
        if (!this.pointerManaged && this.draggingTicketId && this.movable && !this.movesDisabled) {
          event.preventDefault();
          this.nativeOver = true;
          if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
        }
      }}
      @dragleave=${(event: DragEvent) => {
        if (
          !(event.relatedTarget instanceof Node) ||
          !(event.currentTarget as Node).contains(event.relatedTarget)
        )
          this.nativeOver = false;
      }}
      @drop=${(event: DragEvent) => {
        if (this.pointerManaged) return;
        event.preventDefault();
        this.nativeOver = false;
        if (
          this.movable &&
          !this.movesDisabled &&
          this.draggingTicketId &&
          event.dataTransfer?.getData('application/x-dees-kanban-ticket') === this.draggingTicketId
        )
          kanbanEvent(this, 'kanban-ticket-move', {
            ticketId: this.draggingTicketId,
            columnId: this.column!.id,
            beforeTicketId: this.nativeDropBefore,
          });
      }}
    >
      <header>
        <span class="status ${this.column.done ? 'done' : ''}" aria-hidden="true"></span>
        <h3>${this.column.title}</h3>
        <span class="count">${tickets.length}</span>
        <span class="spacer"></span>
        ${this.canCreate
          ? html`<dees-button
              type="ghost"
              size="icon"
              icon="lucide:Plus"
              text=${`Add task to ${this.column.title}`}
              @clicked=${() =>
                kanbanEvent(this, 'kanban-ticket-create', {
                  kind: 'task',
                  columnId: this.column!.id,
                })}
            ></dees-button>`
          : ''}
      </header>
      <div class="stack">${directives.repeat(tickets, ticket => ticket.id, ticket => html`
        <div
          class="target ${this.nativeOver && this.nativeDropBefore === ticket.id
            ? 'native-before'
            : ''}"
          data-ticket-id=${ticket.id}
          @dragover=${() => {
            if (!this.pointerManaged) this.nativeDropBefore = ticket.id;
          }}
        >
          ${motion?.move.ticketId === ticket.id && motion.placeholder
            ? html`<div
                class="placeholder"
                style=${`height: ${motion.height}px`}
                aria-label="Drop ticket here"
              ></div>`
            : html`<dees-kanban-card
                .ticket=${ticket}
                .columns=${this.columns}
                .epic=${this.allTickets.find(
                  item => item.id === ticket.parentId && item.kind === 'epic',
                )}
                .subtasks=${sortKanbanTickets(
                  this.allTickets.filter(
                    item => item.parentId === ticket.id && item.kind === 'subtask',
                  ),
                )}
                .movable=${this.movable && ticket.permissions?.move !== false}
                .pending=${motion?.move.ticketId === ticket.id
                  ? motion.dimmed
                  : this.pendingTicketId === ticket.id}
                .moveDisabled=${this.movesDisabled}
                .saving=${motion?.move.ticketId === ticket.id && motion.saving}
                .pointerManaged=${this.pointerManaged}
              ></dees-kanban-card>`}
        </div>`)}
        ${!tickets.length
          ? html`<div
              class="empty"
              @dragover=${() => {
                this.nativeDropBefore = undefined;
              }}
            >No tasks</div>`
          : ''}
      </div>
      <div
        class="end-target ${this.nativeOver && !this.nativeDropBefore ? 'native-before' : ''}"
        @dragover=${() => {
          this.nativeDropBefore = undefined;
        }}
      ></div>
    </section>`;
  }
}
